import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Simple map using leaflet (open source, mobile friendly)
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { UnifiedMapControls } from './MapControls';

// Import marker icons from leaflet package
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Import logo for zone markers
import logo from '../assets/logo.png';

// Fix default marker icon issue in leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

// Create default icon explicitly (blue marker for user location)
const defaultIcon = L.icon({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

// Create a custom logo icon for zone markers using divIcon with inline styles
const createLogoIcon = (size, borderWidth, highlighted = false) => {
    const imgSize = size * 1.3; // Slightly larger to reduce white space but keep logo visible
    return L.divIcon({
        className: 'custom-logo-marker',
        html: `<div style="
            width: ${size}px;
            height: ${size}px;
            border-radius: 50%;
            border: ${borderWidth}px solid #E5B55F;
            box-shadow: 0 2px 8px rgba(229, 181, 95, ${highlighted ? '0.8' : '0.5'});
            overflow: hidden;
            background: white;
            display: flex;
            align-items: center;
            justify-content: center;
        ">
            <img src="${logo}" style="
                width: ${imgSize}px;
                height: ${imgSize}px;
                object-fit: cover;
            " />
        </div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
        popupAnchor: [0, -(size / 2)]
    });
};

const zoneLogoIcon = createLogoIcon(50, 3, false);
const highlightedLogoIcon = createLogoIcon(60, 4, true);

function MapAutoCenter({ position }) {
    const map = useMap();
    useEffect(() => {
        if (position) {
            map.setView(position, 13);
        }
    }, [position, map]);
    return null;
}

/**
 * NearbyZonesPopup
 * Props:
 *   open: boolean
 *   onClose: function
 *   zones: array of { name, lat, lng }
 *   onSelectZone: function(zoneName)
 *   selectedZones: array of selected zone names
 */
export default function NearbyZonesPopup({ open, onClose, zones, onSelectZone, selectedZones }) {
    const [userLocation, setUserLocation] = useState(null);
    const [geoError, setGeoError] = useState(null);
    const [highlightedZone, setHighlightedZone] = useState(null);

    // Default center: Israel
    const DEFAULT_CENTER = { lat: 31.771959, lng: 35.217018 };

    useEffect(() => {
        if (open) {
            setGeoError(null);
            setUserLocation(null);

            // Check if geolocation is available
            if (!navigator.geolocation) {
                console.warn('Geolocation is not supported by this browser');
                setGeoError('הדפדפן שלך לא תומך במיקום גיאוגרפי. משתמשים במיקום ברירת מחדל.');
                setUserLocation(DEFAULT_CENTER);
                return;
            }

            // Check if we're in a secure context (HTTPS or localhost)
            if (typeof window !== 'undefined' && window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
                console.warn('Geolocation requires HTTPS');
                setGeoError('מיקום גיאוגרפי דורש חיבור מאובטח. משתמשים במיקום ברירת מחדל.');
                setUserLocation(DEFAULT_CENTER);
                return;
            }

            console.log('Requesting geolocation...');

            // Set a fallback timeout
            const fallbackTimeout = setTimeout(() => {
                if (!userLocation) {
                    console.warn('Geolocation timeout - using default location');
                    setUserLocation(DEFAULT_CENTER);
                    if (!geoError) {
                        setGeoError('לא הצלחנו לקבל את מיקומך בזמן. משתמשים במיקום ברירת מחדל.');
                    }
                }
            }, 5000);

            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    console.log('Geolocation success:', pos.coords);
                    clearTimeout(fallbackTimeout);
                    setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                    setGeoError(null);
                },
                (err) => {
                    console.error('Geolocation error:', err);
                    clearTimeout(fallbackTimeout);

                    let errorMsg = 'משתמשים במיקום ברירת מחדל (מרכז ישראל).';
                    if (err.code === 1) {
                        errorMsg = 'הרשאת מיקום נדחתה. ' + errorMsg;
                    } else if (err.code === 2) {
                        errorMsg = 'לא ניתן לקבוע מיקום. ' + errorMsg;
                    } else if (err.code === 3) {
                        errorMsg = 'פג הזמן לקבלת מיקום. ' + errorMsg;
                    }
                    setGeoError(errorMsg);
                    setUserLocation(DEFAULT_CENTER);
                },
                {
                    enableHighAccuracy: false, // Changed to false for better compatibility
                    timeout: 15000, // Increased timeout to 15 seconds
                    maximumAge: 300000 // Allow cached location up to 5 minutes old
                }
            );

            return () => clearTimeout(fallbackTimeout);
        }
    }, [open]);

    // Use the same logic as UserZones.jsx to extract coordinates from zones
    function getZonesWithCoords(zones) {
        if (!zones) return [];
        // If already in {name, lat, lng} format
        if (Array.isArray(zones) && zones.length && zones[0] && typeof zones[0] === 'object' && 'lat' in zones[0] && 'lng' in zones[0]) {
            return zones;
        }
        // Try to parse from array of objects with .location
        if (Array.isArray(zones)) {
            return zones
                .map((zone) => {
                    if (!zone.location) return null;
                    try {
                        const parsed = typeof zone.location === 'string' ? JSON.parse(zone.location) : zone.location;
                        if (Array.isArray(parsed) && parsed.length > 0 && parsed[0]) {
                            return {
                                name: zone.zone_display_name || zone.zone_id || zone.name,
                                lat: parsed[0].latitude,
                                lng: parsed[0].longitude
                            };
                        } else if (parsed && parsed.latitude && parsed.longitude) {
                            return {
                                name: zone.zone_display_name || zone.zone_id || zone.name,
                                lat: parsed.latitude,
                                lng: parsed.longitude
                            };
                        }
                    } catch (e) {
                        // ignore
                        return null;
                    }
                    return null;
                })
                .filter(Boolean);
        }
        // Fallback: try to parse if zones is an object with lat/lng keys
        if (zones && typeof zones === 'object' && !Array.isArray(zones)) {
            return Object.entries(zones)
                .map(([name, val]) => ({ name, ...val }))
                .filter(z => z.lat && z.lng);
        }
        // Fallback: try window.zonesWithCoords (for debugging)
        if (typeof window !== 'undefined' && window.zonesWithCoords) {
            return window.zonesWithCoords;
        }
        return [];
    }

    const zonesWithCoords = getZonesWithCoords(zones);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <motion.div
                        className="bg-white rounded-3xl shadow-2xl max-w-md w-full mx-2 p-4 relative flex flex-col"
                        initial={{ scale: 0.95, y: 40 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.95, y: 40 }}
                        transition={{ duration: 0.3 }}
                    >
                        <h2 className="text-xl font-bold mb-2 text-center">מקומיות בקרבתך</h2>
                        {/* Show error as a warning, not a blocker */}
                        {geoError && (
                            <div className="bg-warning-50 border border-warning-100 text-warning-700 text-center py-2 px-3 rounded-lg text-sm mb-2">
                                {geoError}
                            </div>
                        )}
                        {!userLocation ? (
                            <div className="text-center py-8">טוען מיקום...</div>
                        ) : (
                            <>
                                {(!zonesWithCoords || zonesWithCoords.length === 0) && (
                                    <div className="bg-warning-50 border border-warning-100 text-warning-700 text-center py-2 px-3 rounded-lg text-sm mb-2">
                                        לא נמצאו מקומיות עם מיקום להצגה על המפה.
                                    </div>
                                )}
                                <div className="w-full h-72 rounded-xl overflow-hidden mb-4">
                                    <MapContainer center={userLocation} zoom={13} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
                                        <MapAutoCenter position={userLocation} />
                                        <TileLayer
                                            attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        />
                                        <Marker position={userLocation} icon={defaultIcon}>
                                            <Popup>המיקום שלך</Popup>
                                        </Marker>
                                        {zonesWithCoords.map((zone) => (
                                            <Marker
                                                key={zone.name}
                                                position={{ lat: zone.lat, lng: zone.lng }}
                                                eventHandlers={{
                                                    click: () => setHighlightedZone(zone.name),
                                                }}
                                                icon={highlightedZone === zone.name || selectedZones.includes(zone.name)
                                                    ? highlightedLogoIcon
                                                    : zoneLogoIcon}
                                            >
                                                <Popup>
                                                    <div className="flex flex-col items-center">
                                                        <span className="font-bold text-base mb-1">{zone.name}</span>
                                                        <button
                                                            className={`px-3 py-1 rounded-full text-sm font-semibold mt-1 ${selectedZones.includes(zone.name) ? 'bg-success-500 text-white' : 'bg-warning-500 text-ink-strong'}`}
                                                            onClick={() => onSelectZone(zone.name)}
                                                        >
                                                            {selectedZones.includes(zone.name) ? 'נבחר' : 'בחר מקומית זו'}
                                                        </button>
                                                    </div>
                                                </Popup>
                                            </Marker>
                                        ))}

                                        {/* Modern map controls */}
                                        <UnifiedMapControls
                                            userLocation={[userLocation.lat, userLocation.lng]}
                                            showLayerSwitcher={true}
                                        />
                                    </MapContainer>
                                </div>
                            </>
                        )}
                        <button
                            className="w-full bg-surface-sunken text-ink-strong py-3 rounded-full font-bold shadow hover:shadow-lg hover:-translate-y-[1px] transition-all duration-200 mt-2"
                            onClick={onClose}
                        >
                            סגור
                        </button>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}